import React, { useState, useCallback, useEffect } from 'react';
import * as R from 'ramda';
import { css } from '@emotion/react';
import { useQueryClient } from '@cd/react-query';
import debounce from 'lodash.debounce';
import { CenterProgress } from '../component/CenterProgress';
import Close from '@mui/icons-material/Close';
import {
	PaymentElement,
	useElements,
	useStripe
} from '@stripe/react-stripe-js';
import { useMutationFetch, useQueryFetch } from './useQueryFetch';
import { Button, IconButton } from '@mui/material';
import { useStore } from './store';
const wrapStyle = css({
	padding: '16px',
	display: 'flex',
	flexDirection: 'column',
	minHeight: 320
});

const errorStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: '8px',
	textAlign: 'center',
	background: '#ff5e00',
	borderRadius: '4px',
	marginTop: '8px'
});

const ErrorMsg = ({ error }) => {
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		if (error) {
			setIsOpen(true);
		}
		const id = setTimeout(() => {
			setIsOpen(true);
		}, 5 * 1000);
		return () => clearTimeout(id);
	}, [error]);

	return (isOpen &&
    error &&
    !R.isEmpty(error) && (<div css={errorStyle}>
        <span>
            {R.is(String, error) ? error : JSON.stringify(error)}
        </span>
        <IconButton
            onClick={e => {
                setIsOpen(false);
            }}
            size="large">
            <Close />
        </IconButton>
    </div>));
};

const PaymentMethodSubmitForm = ({ onDone }) => {
	const stripe = useStripe();
	const elements = useElements();
	const [errorMessage, setErrorMessage] = useState(null);
	const [canSubmit, setCanSubmit] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const { dispatch } = useStore();
	const { data, isFetching } = useQueryFetch(
		'appParamStripeRedirect',
		'/payment/method/redirect/url',
		'GET'
	);
	const { mutate, isLoading, error } = useMutationFetch(
		'/payment/method/add',
		{
			onSuccess: v => {
				console.log('onSuccess', v);

				queryClient.invalidateQueries('appPaymentMethods');
				dispatch({
					type: 'post success',
					message: 'You have successfully added a payment method.'
				});
			}
		}
	);

	const onChangeDeb = useCallback(
		debounce(e => {
			setCanSubmit(e.complete);
		}, 250),
		[]
	);
	const queryClient = useQueryClient();

	const handleSubmit = useCallback(
		async event => {
			setIsSaving(true);
			// We don't want to let default form submission happen here,
			// which would refresh the page.
			event.preventDefault();

			if (!stripe || !elements) {
				// Stripe.js hasn't yet loaded.
				// Make sure to disable form submission until Stripe.js has loaded.
				return null;
			}

			const { error, setupIntent } = await stripe.confirmSetup({
				//`Elements` instance that was used to create the Payment Element
				elements,
				redirect: 'if_required',
				confirmParams: {
					return_url: data.PAYMENT_METHOD_REDIRECT_URL
				}
			});

			setIsSaving(false);
			if (error) {
				// This point will only be reached if there is an immediate error when
				// confirming the payment. Show error to your customer (for example, payment
				// details incomplete)
				setErrorMessage(error.message);
			} else {
				// Your customer will be redirected to your `return_url`. For some payment
				// methods like iDEAL, your customer will be redirected to an intermediate
				// site first to authorize the payment, then redirected to the `return_url`.
				const { status, payment_method } = setupIntent;
				if (status === 'succeeded') {
					mutate({ paymentMethodID: payment_method });
					onDone();
				}
			}
		},
		[data]
	);

	if (isFetching) {
		return <CenterProgress />;
	}

	return (
		<form onSubmit={handleSubmit} css={wrapStyle}>
			<PaymentElement onChange={onChangeDeb} />
			<ErrorMsg error={errorMessage || error} />
			<Button
				sx={{ alignSelf: 'flex-end', mt: 2, height: '56px' }}
				type="submit"
				variant="outlined"
				disabled={!canSubmit || isSaving || isLoading}>
				{!isSaving ? 'Submit' : <CenterProgress />}
			</Button>
		</form>
	);
};

export default PaymentMethodSubmitForm;
